import { reactive, watch } from 'vue'

import useRef from '@nicolaudie-group/ng-ui-kit/composables/useRef'
import useComputedToggle from '@nicolaudie-group/ng-ui-kit/composables/useComputedToggle'

import useQuerySync from '@/composables/useQuerySync'

/**
 * @typedef UseAccordionOptions
 * @property {Ref<string>|string} [initial] - ID of collapse that should open initially
 * @property {Function} [onChange] - Method called by each collapse when their status changes
 * @property {string} [query] - Name of the query parameter to sync with the current collapse ID
 */

/**
 * Automatically close adjascent collapses when one is opened
 * @param {String[]} ids - Collapses IDs
 * @param {UseAccordionOptions} [options] - Advanced options for useAccordion
 * @returns
 */
function useAccordion(ids = [], { initial, onChange, query } = {}) {
  /**
   * ID of active collapse
   */
  const current = useRef(initial)

  /**
   * Toggles & controls for the collapses in the accordion
   */
  const collapses = {}

  // For each ID of collapse provided
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i]
    // Create a writable computed toggle for it's state based on the "current" Ref
    const {
      state,
      toggle,
      set,
      on,
      off,
    } = useComputedToggle(
      () => (current.value === id),
      {
        async setter(newState, oldState) {
          return typeof onChange === 'function' ? onChange(id, newState, oldState) : newState
        },
      },
    )

    watch(state, (newState) => {
      if (newState) {
        // We make sure to update the current/query if the collapse is activated
        // (because the change might come from the toggle, and not from the query itself)
        current.value = id
      } else if (current.value === id) {
        // We make sure to clear the current/query if the collapse is not activated
        // and the currently active collapse was this one (in case there is not
        // another collapse opening)
        current.value = undefined
      }
    }, { immediate: true })

    // Create our collapse object that will be returned
    const collapse = {
      state,
      toggle,
      set,
      open: on,
      close: off,
    }

    // Create the control for the collapse and add it to the collapse object
    collapse.control = reactive({
      onToggle: toggle,
      state,
    })

    // Add our collapse to our list of collapses
    collapses[id] = collapse
  }

  // Synchronize the "current" Ref with a query if provided
  if (query) {
    useQuerySync(query, { initial: current })
  }

  // Return the list of collapses + the current collapse
  return {
    collapses,
    current,
  }
}

export default useAccordion
